import { FSD_MARKET_REGIONS } from './lib/fsd-availability-data'
import type { FsdAvailabilityProperties } from './lib/fsd-availability'
import type { FilterState, SegmentRecord, TakeoverPoint, TrustTier } from './lib/types'
import { trustLabel } from './map'

const TRUST_TIERS: TrustTier[] = ['high_trust', 'driven', 'undriven']

const TRUST_COLORS: Record<TrustTier, string> = {
  high_trust: '#5eead4',
  driven: '#60a5fa',
  undriven: '#9ca3af',
}

export interface UIController {
  getFilters: () => FilterState
  onFilterChange: (cb: (filters: FilterState) => void) => void
  showDetail: (html: string) => void
  hideDetail: () => void
  updateStats: (stats: { segments: number; routes: number; coords: number; takeover: number }) => void
  renderLegend: () => void
  renderAttribution: (sources: { id: string; label: string }[]) => void
}

export function createUI(
  regions: string[],
  sources: string[],
): UIController {
  const trustEl = document.getElementById('trust-filters')!
  const regionEl = document.getElementById('region-filters')!
  const sourceEl = document.getElementById('source-filters')!
  const fsdMarketsToggle = document.getElementById('fsd-markets-toggle') as HTMLInputElement
  const takeoverToggle = document.getElementById('takeover-toggle') as HTMLInputElement
  const marketListEl = document.getElementById('fsd-market-list')!
  const detailPanel = document.getElementById('detail-panel')!
  const detailContent = document.getElementById('detail-content')!
  const detailClose = document.getElementById('detail-close')!
  const fabFilters = document.getElementById('fab-filters')!
  const filtersPanel = document.getElementById('filters-panel')!
  const statsEl = document.getElementById('map-stats')!
  const legendEl = document.getElementById('map-legend')!
  const attributionEl = document.getElementById('attribution-list')!

  const state: FilterState = {
    trustTiers: ['high_trust', 'driven', 'undriven'],
    regions: [],
    sources: [],
    showTakeover: true,
    showFsdMarkets: true,
  }

  let changeCb: ((f: FilterState) => void) | null = null

  function emit() {
    changeCb?.({ ...state, trustTiers: [...state.trustTiers], regions: [...state.regions], sources: [...state.sources] })
  }

  function makeChip(label: string, color: string | null, active: boolean, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = `chip${active ? ' active' : ''}`
    if (color) {
      btn.innerHTML = `<span class="chip-dot" style="background:${color}"></span>${label}`
    } else {
      btn.textContent = label
    }
    btn.addEventListener('click', onClick)
    return btn
  }

  function renderTrustChips() {
    trustEl.innerHTML = ''
    for (const tier of TRUST_TIERS) {
      const active = state.trustTiers.includes(tier)
      trustEl.appendChild(makeChip(trustLabel(tier), TRUST_COLORS[tier], active, () => {
        if (active) state.trustTiers = state.trustTiers.filter((t) => t !== tier)
        else state.trustTiers.push(tier)
        renderTrustChips()
        emit()
      }))
    }
  }

  function renderRegionChips() {
    regionEl.innerHTML = ''
    const allActive = state.regions.length === 0
    regionEl.appendChild(makeChip('All regions', null, allActive, () => {
      state.regions = []
      renderRegionChips()
      emit()
    }))
    for (const r of regions) {
      const active = state.regions.includes(r)
      regionEl.appendChild(makeChip(r, null, active, () => {
        if (active) state.regions = state.regions.filter((x) => x !== r)
        else state.regions.push(r)
        renderRegionChips()
        emit()
      }))
    }
  }

  function renderSourceChips() {
    sourceEl.innerHTML = ''
    const allActive = state.sources.length === 0
    sourceEl.appendChild(makeChip('All sources', null, allActive, () => {
      state.sources = []
      renderSourceChips()
      emit()
    }))
    for (const s of sources) {
      const active = state.sources.includes(s)
      sourceEl.appendChild(makeChip(s.replace(/_/g, ' '), null, active, () => {
        if (active) state.sources = state.sources.filter((x) => x !== s)
        else state.sources.push(s)
        renderSourceChips()
        emit()
      }))
    }
  }

  renderTrustChips()
  renderRegionChips()
  renderSourceChips()

  fsdMarketsToggle.addEventListener('change', () => {
    state.showFsdMarkets = fsdMarketsToggle.checked
    emit()
  })

  takeoverToggle.addEventListener('change', () => {
    state.showTakeover = takeoverToggle.checked
    emit()
  })

  function renderMarketList() {
    const order = { active: 0, limited: 1, pending: 2 } as const
    const sorted = [...FSD_MARKET_REGIONS].sort(
      (a, b) => order[a.status] - order[b.status] || a.name.localeCompare(b.name),
    )
    marketListEl.innerHTML = sorted
      .map((m) => `<li class="market-item market-${m.status}"><span class="market-dot"></span>${m.name}</li>`)
      .join('')
  }
  renderMarketList()

  detailClose.addEventListener('click', () => hideDetail())

  fabFilters.addEventListener('click', () => {
    const open = filtersPanel.classList.toggle('open')
    fabFilters.setAttribute('aria-expanded', String(open))
  })

  function hideDetail() {
    detailPanel.hidden = true
  }

  return {
    getFilters: () => ({ ...state }),
    onFilterChange(cb) { changeCb = cb },
    showDetail(html) {
      detailContent.innerHTML = html
      detailPanel.hidden = false
    },
    hideDetail,
    updateStats({ segments, routes, coords, takeover }) {
      statsEl.innerHTML = `
        <div class="stat-grid">
          <div class="stat-item">
            <span class="stat-value">${segments.toLocaleString()}</span>
            <span class="stat-label">Corridor segments</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">${routes}</span>
            <span class="stat-label">Community routes</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">${coords.toLocaleString()}</span>
            <span class="stat-label">Coordinates</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">${takeover}</span>
            <span class="stat-label">Takeover clusters</span>
          </div>
        </div>
      `
    },
    renderLegend() {
      legendEl.innerHTML = TRUST_TIERS.map((tier) =>
        `<div class="legend-item"><span class="legend-swatch" style="background:${TRUST_COLORS[tier]}"></span>${trustLabel(tier)}</div>`,
      ).join('') + `
        <div class="legend-item"><span class="legend-swatch market-active"></span>FSD active market</div>
        <div class="legend-item"><span class="legend-swatch market-limited"></span>FSD limited rollout</div>
        <div class="legend-item"><span class="legend-swatch market-pending"></span>FSD pending approval</div>
        <div class="legend-item"><span class="legend-swatch point" style="background:#f472b6"></span>Takeover point</div>
        <div class="legend-item"><span class="legend-swatch point" style="background:#f59e0b"></span>Hazard marker</div>
      `
    },
    renderAttribution(sources) {
      attributionEl.innerHTML = sources
        .map((s) => `<li>${s.label} (${s.id})</li>`)
        .concat(['<li>OpenStreetMap basemap</li>', '<li>Community DE samples (ladnishad)</li>'])
        .join('')
    },
  }
}

export function segmentDetailHtml(props: SegmentRecord): string {
  return `
    <h3 class="detail-title">Road corridor</h3>
    <span class="detail-badge ${props.trust}">${trustLabel(props.trust)}</span>
    <div class="detail-grid">
      <div class="detail-row"><span>Sample points</span><span>${props.sampleCount}</span></div>
      <div class="detail-row"><span>Data sources</span><span>${props.sourceCount}</span></div>
      ${props.region ? `<div class="detail-row"><span>Region</span><span>${props.region}</span></div>` : ''}
      ${props.fsdPercentage != null ? `<div class="detail-row"><span>FSD %</span><span>${props.fsdPercentage.toFixed(1)}%</span></div>` : ''}
      <div class="detail-row"><span>Sources</span><span>${props.sources.join(', ')}</span></div>
    </div>
    <p class="detail-note">Community coverage signal — not a safety rating. Informational, not navigation.</p>
  `
}

export function fsdMarketDetailHtml(props: FsdAvailabilityProperties): string {
  const statusLabel =
    props.status === 'active' ? 'Active market'
      : props.status === 'limited' ? 'Limited rollout'
        : 'Pending approval'
  return `
    <h3 class="detail-title">${props.name}</h3>
    <span class="detail-badge fsd_${props.status}">${statusLabel}</span>
    <div class="detail-grid">
      <div class="detail-row"><span>Status</span><span>${statusLabel}</span></div>
      ${props.notes ? `<div class="detail-row"><span>Notes</span><span>${props.notes}</span></div>` : ''}
    </div>
    <p class="detail-note">Based on Tesla support pages and community regulatory trackers. Not an official Tesla product. Informational only.</p>
  `
}

export function takeoverDetailHtml(props: TakeoverPoint & { clusterSize?: number; members?: string[] }): string {
  const layerLabel = props.layer === 'hazard' ? 'Hazard marker' : 'Takeover point'
  return `
    <h3 class="detail-title">${props.label}</h3>
    <span class="detail-badge ${props.layer}">${layerLabel}</span>
    <div class="detail-grid">
      <div class="detail-row"><span>Category</span><span>${props.category.replace(/_/g, ' ')}</span></div>
      ${props.date ? `<div class="detail-row"><span>Date</span><span>${props.date}</span></div>` : ''}
      <div class="detail-row"><span>Source</span><span>${props.source}</span></div>
      ${props.clusterSize && props.clusterSize > 1 ? `<div class="detail-row"><span>Clustered signals</span><span>${props.clusterSize}</span></div>` : ''}
      <div class="detail-row"><span>Coordinates</span><span>${props.lat.toFixed(4)}, ${props.lng.toFixed(4)}</span></div>
    </div>
    <p class="detail-note">Historical community signal — not a safety score. Other drivers reported needing manual control near this location.</p>
  `
}